import React, { useEffect, useState } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
} from "@material-ui/core";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { toast } from "react-toastify";
import useCompanies from "../../hooks/useCompanies";
import useModules from "../../hooks/useModules";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    width: "100%",
    flex: 1,
    padding: theme.spacing(2),
  },
  tableContainer: {
    width: "100%",
    overflowX: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const CompanyModulesManager = () => {
  const classes = useStyles();
  const { list: listCompanies } = useCompanies();
  const { getCompanyModules, updateCompanyModules } = useModules();

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const companyList = await listCompanies();
        setCompanies(companyList);
      } catch (e) {
        toast.error(i18n.t("companyModulesManager.toasts.companiesLoadError"));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedCompany) return;
    (async () => {
      setLoading(true);
      try {
        const response = await getCompanyModules(selectedCompany);
        setModules(response.modules || []);
      } catch (e) {
        toast.error(i18n.t("companyModulesManager.toasts.modulesLoadError"));
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  const handleOverrideChange = (id, value) => {
    setModules((prev) =>
      prev.map((m) =>
        m.id === id ? { ...m, companyOverride: value } : m
      )
    );
  };

  const handleSave = async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const payload = modules.map((m) => ({
        moduleId: m.id,
        key: m.key,
        enabled: m.companyOverride,
        source: m.companyOverride !== null ? "manual" : undefined,
      }));
      const response = await updateCompanyModules(selectedCompany, payload);
      if (response?.modules) {
        setModules(response.modules);
      }
      toast.success(i18n.t("companyModulesManager.toasts.saved"));
    } catch (e) {
      toast.error(i18n.t("companyModulesManager.toasts.saveError"));
    }
    setLoading(false);
  };

  return (
    <Paper className={classes.mainPaper} elevation={0}>
      <Grid spacing={2} container>
        <Grid xs={12} md={4} item>
          <FormControl fullWidth variant="outlined" margin="dense">
            <InputLabel>{i18n.t("common.company")}</InputLabel>
            <Select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              label={i18n.t("common.company")}
            >
              {companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid xs={12} item>
          <Paper className={classes.tableContainer}>
            <Table padding="none">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("companyModulesManager.table.module")}</TableCell>
                  <TableCell>{i18n.t("companyModulesManager.table.category")}</TableCell>
                  <TableCell align="center">{i18n.t("companyModulesManager.table.plan")}</TableCell>
                  <TableCell align="center">{i18n.t("companyModulesManager.table.override")}</TableCell>
                  <TableCell align="center">{i18n.t("companyModulesManager.table.effective")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modules.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell>{m.name}</TableCell>
                    <TableCell>{m.category}</TableCell>
                    <TableCell align="center">{m.planIncluded ? i18n.t("common.yes") : i18n.t("common.no")}</TableCell>
                    <TableCell align="center">
                      <FormControl variant="outlined" size="small">
                        <Select
                          value={
                            m.companyOverride === null || m.companyOverride === undefined
                              ? "plan"
                              : m.companyOverride
                          }
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "plan") {
                              handleOverrideChange(m.id, null);
                            } else {
                              handleOverrideChange(m.id, val === true || val === "true");
                            }
                          }}
                        >
                          <MenuItem value="plan">{i18n.t("companyModulesManager.override.plan")}</MenuItem>
                          <MenuItem value={true}>{i18n.t("companyModulesManager.override.enable")}</MenuItem>
                          <MenuItem value={false}>{i18n.t("companyModulesManager.override.disable")}</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell align="center">{m.effective ? i18n.t("common.yes") : i18n.t("common.no")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
        <Grid xs={12} item>
          <ButtonWithSpinner
            variant="contained"
            color="primary"
            onClick={handleSave}
            loading={loading}
          >
            {i18n.t("companyModulesManager.buttons.saveChanges")}
          </ButtonWithSpinner>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default CompanyModulesManager;
